import { useState } from "react";
import { BookOpen, Check, Download, Mail } from "lucide-react";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function EbookCTA() {
  const { lang } = useLang();
  const t = useDict(lang);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | ok | err
  const [downloadUrl, setDownloadUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const res = await api.requestEbook(email, lang);
      setDownloadUrl(`${BACKEND}${res.download_url}`);
      setState("ok");
    } catch {
      setState("err");
    }
  };

  return (
    <section data-testid="ebook-section" className="mt-32 relative overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none"
        style={{ backgroundImage: "url(/api/static/recipes/jollof-rice.png)" }}
      />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="bg-[color:var(--ink)] text-[color:var(--bg)] grid lg:grid-cols-12 gap-10 px-8 lg:px-16 py-16 lg:py-20">
          {/* Visual */}
          <div className="lg:col-span-5 flex items-center justify-center">
            <div className="relative">
              <div className="absolute -inset-3 bg-[color:var(--red)] rotate-[-3deg]" />
              <div className="relative bg-[color:var(--bg)] text-[color:var(--ink)] p-8 w-[260px] aspect-[3/4] flex flex-col justify-between rotate-[2deg]">
                <div>
                  <div className="flex items-center gap-2 mb-6">
                    <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--red)] block" />
                    <span className="font-serif-display text-lg leading-none">BAYO</span>
                  </div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--ink-2)]">Édition gratuite N° 01</p>
                </div>
                <div>
                  <p className="font-serif-display text-3xl leading-[1.05]">10 recettes</p>
                  <p className="font-serif-display text-3xl leading-[1.05]">caribéennes</p>
                  <p className="font-serif-display text-3xl leading-[1.05] italic text-[color:var(--red)]">faciles.</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--ink-2)] mt-4">40 min ou moins · PDF</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-xs tracking-[0.28em] uppercase opacity-70 mb-5">
              <BookOpen size={14} />
              <span>{t.ebook.eyebrow}</span>
            </div>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] mb-6">{t.ebook.title}</h2>
            <p className="text-base opacity-80 max-w-xl leading-relaxed mb-8">{t.ebook.sub}</p>

            {state !== "ok" ? (
              <form onSubmit={submit} className="space-y-4 max-w-xl">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex items-center gap-3 border-b border-[color:var(--bg)] py-3">
                    <Mail size={18} className="opacity-70" />
                    <input
                      data-testid="ebook-email"
                      required
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.ebook.placeholder}
                      className="flex-1 bg-transparent outline-none placeholder:text-white/50 text-white"
                    />
                  </div>
                  <button
                    data-testid="ebook-submit"
                    disabled={state === "sending"}
                    type="submit"
                    className="bg-[color:var(--red)] text-white px-7 py-3 text-xs tracking-[0.22em] uppercase font-medium hover:bg-[color:var(--red-hover)] transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap"
                  >
                    {state === "sending" ? "..." : <>{t.ebook.cta} <Download size={14} /></>}
                  </button>
                </div>
                <p className="text-xs opacity-50 max-w-md">{t.ebook.consent}</p>
                {state === "err" && <p className="text-xs text-[color:var(--mango)]">{t.ebook.error}</p>}
              </form>
            ) : (
              <div data-testid="ebook-success" className="border border-[color:var(--bg)] border-opacity-30 p-6 max-w-xl">
                <p className="text-xs tracking-[0.22em] uppercase text-[color:var(--mango)] mb-2 inline-flex items-center gap-2">
                  <Check size={14} /> {t.ebook.success}
                </p>
                <p className="text-base opacity-90 mb-5">{t.ebook.successSub}</p>
                <a
                  data-testid="ebook-download"
                  href={downloadUrl}
                  download
                  className="bg-[color:var(--bg)] text-[color:var(--ink)] px-7 py-3 text-xs tracking-[0.22em] uppercase font-medium hover:bg-white transition-colors inline-flex items-center gap-2"
                >
                  <Download size={14} /> {t.ebook.downloadNow}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
